import { Link } from 'react-router-dom';

import { brand } from '../../config/brand';
import { legal } from '../../config/legal';
import { Clause, LegalPage, Points } from './LegalPage';
import { useDocumentTitle } from '../../lib/title';

/**
 * The privacy policy.
 *
 * ## It describes what the code actually does
 *
 * Every claim here is checkable against the implementation: listener keys are
 * salted daily and never stored alongside an account, IP addresses are hashed
 * before they touch a table, the refresh token is an httpOnly cookie the page
 * cannot read, suppression lists store a hash rather than an address. Writing
 * "we take your privacy seriously" and stopping would have been shorter and
 * would have told nobody anything.
 *
 * Where a practice would be surprising - counting a play without knowing who
 * played it, keeping a removed chat message's row - it is said plainly rather
 * than left for somebody to discover.
 */
export function PrivacyPage() {
  useDocumentTitle('Privacy');

  return (
    <LegalPage
      title="Privacy Policy"
      summary={`We collect what the service needs to work and very little else. Play counts are
        measured without knowing who you are - listeners are counted using a key that is salted
        fresh each day and cannot be tied back to a person. We do not sell your data, we do not
        run advertising trackers, and the only third parties involved are the ones that store
        files, deliver email, carry live updates and count page visits without cookies.`}
    >
      <Clause id="who" title="1. Who is responsible">
        <p>
          {legal.operator} operates {brand.name} and decides how the data described here is
          handled. For data protection questions, write to {legal.contact.privacy}.
        </p>
      </Clause>

      <Clause id="what" title="2. What we collect">
        <p className="font-medium text-ink">Because you gave it to us</p>
        <Points>
          <li>
            Your email address and a hashed password. We never store the password itself and
            cannot recover it.
          </li>
          <li>
            Your handle, display name, and anything you choose to add to your profile: a
            biography, a location, links, an avatar and a banner.
          </li>
          <li>
            Everything you upload or write - tracks, artwork, descriptions, comments, messages,
            room chat, host applications and copyright correspondence.
          </li>
        </Points>

        <p className="mt-2 font-medium text-ink">Because the service produced it</p>
        <Points>
          <li>
            Plays, saves, reposts, follows and listening history needed to build feeds, charts and
            your own statistics.
          </li>
          <li>
            Technical records of uploads and transcoding, so a failed conversion can be
            diagnosed and retried.
          </li>
          <li>
            Delivery outcomes for email we send you - whether it arrived, bounced, or was reported
            as spam.
          </li>
          <li>
            A record of moderation decisions affecting your account, including copyright claims
            and their outcomes.
          </li>
        </Points>

        <p className="mt-2 font-medium text-ink">Deliberately not collected</p>
        <Points>
          <li>
            No advertising trackers, no cross-site fingerprinting, no data brokers. We count
            page visits with Cloudflare Web Analytics, which sets no cookies, stores no
            identifier for you, and cannot follow you to any other site.
          </li>
          <li>
            No payment card details. We do not process payments.
          </li>
          <li>
            No precise location. A location on your profile is text you typed, not something we
            measured.
          </li>
        </Points>
      </Clause>

      <Clause id="counting" title="3. How listening is counted">
        <p>
          A play is recorded so an artist can see that somebody listened, and so charts can be
          built. It is deliberately recorded in a way that cannot be traced back to a person.
        </p>
        <p>
          Instead of storing who you are, we derive a listener key by hashing a combination of
          your network address and browser details together with a secret that is regenerated
          every day. The key lets us tell two plays apart within a day without knowing whose they
          are, and it stops being linkable to yesterday's the moment the salt rotates. The same
          method counts who is in a live room.
        </p>
        <p>
          Where we log an IP address at all - filing a copyright notice, an administrative action
          - it is hashed before it is written down. The original is not stored.
        </p>
      </Clause>

      <Clause id="why" title="4. Why we use it">
        <Points>
          <li>
            <span className="text-ink">To run the service</span> - authenticate you, serve audio,
            build feeds and charts, deliver live rooms.
          </li>
          <li>
            <span className="text-ink">To tell you things you asked to know</span> - somebody
            saved your track, a host played it, your upload finished processing.
          </li>
          <li>
            <span className="text-ink">To keep it safe and honest</span> - detect manipulated play
            counts, handle reports and copyright claims, enforce our terms.
          </li>
          <li>
            <span className="text-ink">To meet legal obligations</span> - keep records of
            copyright notices and the decisions made on them.
          </li>
        </Points>
        <p>
          Where the law requires a lawful basis, ours is the performance of our contract with you
          for running the service, our legitimate interest in keeping it working and free of
          abuse, and legal obligation for the copyright records.
        </p>
      </Clause>

      <Clause id="public" title="5. What other people can see">
        <p>
          Your handle, display name, profile, public tracks, albums, comments, reposts, follower
          and following lists, and anything you say in room chat are visible to anyone - including
          people without an account, and search engines.
        </p>
        <p>
          Unlisted tracks are not shown on your profile or in discovery but can be played by
          anybody with the link. Private tracks are visible only to you.
        </p>
        <p>
          Your email address is never shown to other users. Direct messages are visible to the
          people in the conversation and, where a report requires it, to moderators.
        </p>
        <p>
          A removed chat message keeps its place in the conversation with its contents gone, so
          that a moderated room still reads in order rather than appearing to have gaps.
        </p>
      </Clause>

      <Clause id="cookies" title="6. Cookies and similar storage">
        <Points>
          <li>
            A session cookie holding your refresh token. It is httpOnly, so the page itself cannot
            read it - which is what stops a script injected into the site from stealing your
            session.
          </li>
          <li>
            A short-lived media token cookie that authorizes audio segments while you listen.
          </li>
          <li>
            Local storage holding your volume, and where you were in the current track, so a
            refresh does not lose your place.
          </li>
          <li>
            Local storage holding your last few searches, so the box can offer them back. It
            stays on this device, is never sent to us, and there is a Clear button on the list
            itself.
          </li>
        </Points>
        <p>
          All of these are necessary for the service to function. We set no advertising or
          analytics cookies - the visit counting described above works without one, which is
          why it is not in this list and why there is no cookie banner on this site.
        </p>
      </Clause>

      <Clause id="sponsorships" title="7. Sponsorships">
        <p>
          Some pages carry a sponsorship card, always labelled as one. There is no ad network
          involved: the picture is a file on our own storage, the click is a redirect through
          our own API, and no third-party script runs on this site.
        </p>
        <p>
          Two things are counted - how many times a card was actually on somebody's screen, and
          how many times it was clicked. Both are stored as daily totals against the campaign,
          not against you.
        </p>
        <p>
          So that the same card is not shown to one person twenty times a day, we keep a count
          for the day against a short code. That code is a one-way hash of your account, or of
          your IP address and browser string if you are signed out, mixed with the date and a
          secret of ours. It cannot be turned back into an address, it is different tomorrow for
          the same person, and the rows are deleted after a few days. Nothing about a
          sponsorship is shared with the advertiser beyond those totals, and the page you
          clicked from is not passed on.
        </p>
      </Clause>

      <Clause id="processors" title="8. Who else is involved">
        <p>
          We use a small number of providers, each for one job, and share only what that job
          needs:
        </p>
        <Points>
          <li>
            <span className="text-ink">Object storage and delivery</span> - holds your audio,
            artwork and other files and serves them to listeners.
          </li>
          <li>
            <span className="text-ink">Email delivery</span> - sends verification, password reset
            and notification email, and tells us whether it arrived.
          </li>
          <li>
            <span className="text-ink">Realtime messaging</span> - carries live updates for rooms
            and notifications. It transmits events; it is never the record of anything.
          </li>
        </Points>
        <p>
          None of them are permitted to use your data for their own purposes. We do not sell
          personal data, and we have never done so.
        </p>
      </Clause>

      <Clause id="email" title="9. Email you receive, and stopping it">
        <p>
          We send email for things you need - verifying your address, resetting a password - and
          for activity you asked to hear about. Every optional message can be turned off.
        </p>
        <p>
          If a message hard-bounces or you mark one as spam, we add your address to a suppression
          list and stop sending. That list stores a hash of the address rather than the address
          itself, so honoring your complaint does not require us to keep a copy of your email in
          another table.
        </p>
      </Clause>

      <Clause id="retention" title="10. How long we keep things">
        <Points>
          <li>
            Account data, for as long as your account exists.
          </li>
          <li>
            Deleted content, removed from our systems including backups within 30 days.
          </li>
          <li>
            Play records, aggregated into hourly totals; the individual rows behind older
            statistics are pruned once they are no longer needed to compute them.
          </li>
          <li>
            Sponsorship counts, kept as daily totals per campaign; the short codes behind the
            frequency limit are deleted after a few days.
          </li>
          <li>
            Copyright notices and their outcomes, kept as long as we may need to evidence how a
            claim was handled.
          </li>
          <li>
            Moderation records, kept while relevant to enforcement, and strikes for{' '}
            {legal.strikeMonths} months.
          </li>
        </Points>
        <p>
          When you close your account we delete your content and profile. Some records survive in
          anonymised or minimal form - a comment thread keeps the shape of the conversation, and
          copyright records keep the fact that a claim was made and resolved.
        </p>
      </Clause>

      <Clause id="rights" title="11. Your rights">
        <p>
          Depending on where you live you may have the right to see what we hold about you,
          correct it, delete it, take it elsewhere in a portable form, or object to certain uses.
          You can exercise all of these by writing to {legal.contact.privacy}, and we will respond
          within 30 days.
        </p>
        <p>
          Most of it you can do yourself: your profile is editable, your tracks are deletable, and
          closing your account removes your content.
        </p>
        <p>
          If you are in the UK or EU and think we have handled your data badly, you may complain
          to your national data protection authority. We would rather you told us first.
        </p>
      </Clause>

      <Clause id="security" title="12. Security">
        <p>
          Passwords are hashed with a modern algorithm designed to be slow. Sessions use
          short-lived access tokens with a refresh token the page cannot read. Audio is served
          behind a token check rather than from an open bucket. Access to production data is
          limited to the people who need it, and administrative actions are recorded in an audit
          log.
        </p>
        <p>
          No service is perfectly secure. If we discover a breach affecting your data we will tell
          you and the relevant authority within the time the law requires.
        </p>
      </Clause>

      <Clause id="children" title="13. Children">
        <p>
          The service is not for children under 13, or under 16 where local law sets a higher age
          for consenting to this kind of processing. If we learn we hold data for somebody below
          that age we delete the account.
        </p>
      </Clause>

      <Clause id="changes" title="14. Changes">
        <p>
          If we change this policy in a way that materially affects you, we will tell you in the
          product before it takes effect. The date at the top is always the version in force.
        </p>
        <p>
          See also our{' '}
          <Link to="/terms" className="text-accent hover:underline">
            Terms of Service
          </Link>
          .
        </p>
      </Clause>
    </LegalPage>
  );
}
